import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ScrollText, X } from "lucide-react";

/** Renders a versioned legal agreement fetched from /api/legal/{key}.
 *  Shows the plain-language summary first (clearly labelled), then the full
 *  agreement, which controls. */
export function AgreementBody({ doc }) {
  return (
    <div data-testid="agreement-body">
      {doc.effective_date && (
        <p className="text-xs font-semibold uppercase tracking-wider text-[#2D2926]/45 mb-4" data-testid="agreement-effective-date">
          Effective date: {doc.effective_date}
        </p>
      )}

      {doc.summary?.length > 0 && (
        <div className="mb-8 rounded-2xl bg-[#F2F6F3] border border-[#245544]/15 p-5" data-testid="agreement-summary">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#245544] mb-2">Plain-language summary</h3>
          {doc.summary.map((p, i) => (
            <p key={i} className="text-sm text-[#2D2926]/75 leading-relaxed mb-2 last:mb-0">{p}</p>
          ))}
          <p className="text-[11px] text-[#2D2926]/50 mt-3">
            This summary is for convenience only. The full agreement below is what controls.
          </p>
        </div>
      )}

      {doc.sections.length > 3 && (
        <nav className="mb-8" data-testid="agreement-toc">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#2D2926]/45 mb-2">Contents</h3>
          <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
            {doc.sections.map((s, i) => (
              <li key={i}>
                <a href={`#s-${i}`} className="text-sm text-[#245544] hover:underline">{s.heading}</a>
              </li>
            ))}
          </ol>
        </nav>
      )}

      {doc.sections.map((s, i) => (
        <div key={i} id={`s-${i}`} className="mb-6 scroll-mt-24">
          <h4 className="font-bold text-[15px] mb-2 text-[#2D2926]" style={{ fontFamily: "Outfit" }}>{s.heading}</h4>
          {s.body.map((p, j) => (
            <p key={j} className="text-sm text-[#2D2926]/70 leading-relaxed mb-2.5">{p}</p>
          ))}
        </div>
      ))}

      <p className="text-[11px] text-[#2D2926]/45 pt-2 border-t border-black/[0.06]" data-testid="agreement-version">
        Version {doc.version}
        {doc.effective_date ? ` · Effective ${doc.effective_date}` : ""}
        {" · Not attorney-reviewed."}
      </p>
    </div>
  );
}

/** Legacy pros have no stored acceptance. Nothing is recorded until they have
 *  actually seen this agreement and explicitly ticked the box — closing or
 *  cancelling leaves the requirement incomplete. */
export default function ProTermsModal({ onClose, onAccepted }) {
  const [doc, setDoc] = useState(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/legal/pro_terms").then(r => setDoc(r.data))
      .catch(() => toast.error("Could not load the LIMYA Pro Terms"));
  }, []);

  const accept = async () => {
    setBusy(true);
    try {
      const r = await api.post("/pros/me/terms-accept", { agreement_version: doc.version, agree: true });
      toast.success("LIMYA Pro Terms accepted");
      onAccepted?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record your acceptance");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/45 grid place-items-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="pro-terms-modal">
        <div className="flex items-start justify-between gap-3 p-6 pb-3 border-b border-black/[0.07]">
          <div className="flex items-center gap-2">
            <ScrollText size={18} className="text-[#245544]"/>
            <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>{doc?.title || "LIMYA Pro Terms"}</h3>
          </div>
          <button onClick={onClose} className="text-[#2D2926]/45 hover:text-[#2D2926]" data-testid="pro-terms-close"><X size={18}/></button>
        </div>

        <div className="overflow-y-auto px-6 py-4 flex-1">
          {doc ? <AgreementBody doc={doc}/> : <p className="text-sm text-[#2D2926]/55">Loading…</p>}
        </div>

        <div className="p-6 pt-3 border-t border-black/[0.07]">
          <label className="flex items-start gap-2.5 text-sm cursor-pointer mb-3">
            <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#2E6B57]" data-testid="pro-terms-agree-checkbox"/>
            <span className="text-[#2D2926]/75">I have read and agree to the LIMYA Pro Terms.</span>
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm" data-testid="pro-terms-cancel">Cancel</button>
            <button onClick={accept} disabled={!agree || !doc || busy}
              className="btn-lime px-5 py-2 text-sm disabled:opacity-50" data-testid="pro-terms-accept">
              {busy ? "Saving…" : "Accept"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
